const EVENT_INTRO_LABELS = ['事件名称', '发生日期', '发生地点', '来源/依据', '来源依据']
const SOURCE_LABEL_PATTERN = /来源|依据/
const PROVINCE_LABEL_PATTERN = /^省份$/
const PROVINCE_CODE_PATTERN = /^(province|shengfen|shengfen|jiguan)$/i

export function isSourceField(field) {
  const label = (field && field.label) || ''
  const code = ((field && field.code) || '').toLowerCase()
  const valueRange = ((field && field.valueRange) || '').toLowerCase()
  return SOURCE_LABEL_PATTERN.test(label)
    || /laiyuan|source|yiju|evidence/.test(code)
    || valueRange === 'evidence'
}

export function filterEventIntroFields(fields) {
  const list = (fields || []).filter((field) => field && field.label)
  const matched = EVENT_INTRO_LABELS
    .map((label) => list.find((field) => normalizeLabel(field.label) === normalizeLabel(label)
      || (label === '来源/依据' && isSourceField(field))))
    .filter(Boolean)
    .filter((field, index, arr) => arr.findIndex((item) => item.label === field.label) === index)
  return matched
}

function normalizeLabel(label) {
  return String(label || '').replace(/\s+/g, '').replace(/／/g, '/')
}

export function renameProvinceToNativePlace(fields) {
  return (fields || []).map((field) => {
    const label = field.label || ''
    const code = field.code || ''
    if (PROVINCE_LABEL_PATTERN.test(label) || PROVINCE_CODE_PATTERN.test(code)) {
      return { ...field, label: '籍贯' }
    }
    return field
  })
}

export function splitSourceValues(value) {
  if (!value || value === '-') {
    return []
  }
  return String(value)
    .split(/[,，、;；|]/)
    .map((part) => part.trim())
    .filter((part) => part && part !== '-')
}

/**
 * 解析来源/依据字段为可跳转链接。
 * 优先使用接口返回的 resourceLinks；否则从字段值 / 关联资源中解析。
 */
export function resolveSourceLinks(field, resources) {
  const apiLinks = normalizeApiResourceLinks(field && field.resourceLinks)
  if (apiLinks.length) {
    return apiLinks.map((item) => ({
      ...item,
      text: displaySourceText(item.text)
    }))
  }

  const parsed = parseSourceItems(field && field.value)
  if (!parsed.length) {
    return []
  }

  const list = resources || []
  return parsed.map((item) => {
    if (item.resourceId) {
      const matched = list.find((resource) => Number(resource.id) === Number(item.resourceId))
      return {
        text: displaySourceText(item.text || (matched && matched.title) || String(item.resourceId)),
        resourceId: Number(item.resourceId)
      }
    }

    const exact = list.find((resource) => (
      resource.title === item.text
      || resource.title === String(item.text || '').replace(/^《|》$/g, '')
      || resource.title === normalizeSourceToken(item.text)
    ))
    if (exact) {
      return {
        text: exact.title || displaySourceText(item.text),
        resourceId: exact.id
      }
    }
    const fuzzy = matchResourceBySourceName(item.text, list)
    return {
      text: (fuzzy && fuzzy.title) || displaySourceText(item.text),
      resourceId: fuzzy ? fuzzy.id : null
    }
  })
}

export function displaySourceText(text) {
  return normalizeSourceToken(text) || String(text || '').trim()
}

function normalizeApiResourceLinks(links) {
  if (!Array.isArray(links) || !links.length) {
    return []
  }
  return links
    .map((item) => {
      const resourceId = Number(item && (item.resourceId || item.id))
      if (!Number.isFinite(resourceId) || resourceId <= 0) {
        return null
      }
      return {
        text: (item && (item.title || item.name || item.text)) || String(resourceId),
        resourceId
      }
    })
    .filter(Boolean)
}

function parseSourceItems(value) {
  if (!value || value === '-') {
    return []
  }
  const raw = String(value).trim()
  if (!raw) {
    return []
  }

  if (raw.startsWith('[') || raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.map(mapSourceItem).filter(Boolean)
      }
      const single = mapSourceItem(parsed)
      return single ? [single] : []
    } catch (error) {
      // fall through to plain text parsing
    }
  }

  return splitSourceValues(raw).map((text) => {
    const resourceId = parseResourceId(text)
    return {
      text,
      resourceId
    }
  })
}

function mapSourceItem(item) {
  if (item == null) {
    return null
  }
  if (typeof item === 'number' || (typeof item === 'string' && /^\d+$/.test(item.trim()))) {
    const resourceId = Number(item)
    if (!Number.isFinite(resourceId) || resourceId <= 0) {
      return null
    }
    return { text: String(resourceId), resourceId }
  }
  if (typeof item === 'object') {
    const resourceId = Number(
      item.resourceId
      || item.resource_id
      || item.documentId
      || item.document_id
      || item.id
    )
    const text = item.title || item.name || item.fileName || item.url || item.text
    if (Number.isFinite(resourceId) && resourceId > 0) {
      return {
        text: text || String(resourceId),
        resourceId
      }
    }
    if (text) {
      return { text, resourceId: null }
    }
    return null
  }
  const text = String(item).trim()
  if (!text) {
    return null
  }
  return {
    text,
    resourceId: parseResourceId(text)
  }
}

function parseResourceId(text) {
  const trimmed = String(text || '').trim()
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed)
  }
  const matched = trimmed.match(/(?:资源ID|resourceId|documentId|id)[:：#]?\s*(\d+)/i)
  return matched ? Number(matched[1]) : null
}

/** 去掉 .md 等后缀，便于按文件名匹配资源标题 */
export function normalizeSourceToken(text) {
  return String(text || '')
    .trim()
    .replace(/\.(md|txt|docx?|pdf)$/i, '')
    .trim()
}

/**
 * 在候选资源中按标题前缀匹配来源文件名（取最长标题命中）。
 * 例：江南大学纪事（1958-2017）_江南大学纪事_181-360.md → 江南大学纪事（1958-2017）
 */
export function matchResourceBySourceName(token, resources) {
  const normalized = normalizeSourceToken(token)
  if (!normalized || !Array.isArray(resources) || !resources.length) {
    return null
  }
  let best = null
  resources.forEach((item) => {
    const title = String((item && item.title) || '').trim()
    if (!title) {
      return
    }
    const hit = normalized === title
      || normalized.startsWith(`${title}_`)
      || normalized.startsWith(`${title}.`)
    if (!hit) {
      return
    }
    if (!best || title.length > String(best.title || '').length) {
      best = item
    }
  })
  return best
}

/** 用于搜索的关键词：优先取文件名第一段 */
export function sourceSearchKeyword(token) {
  const normalized = normalizeSourceToken(token)
  if (!normalized) {
    return ''
  }
  const first = normalized.split(/[_／/]/)[0]
  return (first || normalized).trim()
}
